import { ChevronLeft, ChevronRight, RefreshCw } from 'lucide-react'

import { Button, SelectField, StatusMessage } from '@krp/ui-kit'

import { ALL_PRICE_TYPES, type PriceListActions, type PriceListVm } from '../model/priceList'
import type { PriceManageActions, PriceManageVm } from '../model/priceManage'
import { FreshnessBadge } from './FreshnessBadge'
import { PriceCreatePanel, PriceRemoveCell } from './PriceManage'
import { QueryState } from './QueryState'
import './onec-ui.css'

/**
 * Цены базы 1С — как регистр цен в 1С (замечание владельца 18.10.2026): один список записей всех видов цен (розничные,
 * закупочные, любые другие) — вид цен, товар, цена, дата; фильтр — по виду цен. Ни номенклатуры, ни поиска, ни остатков.
 * «Создать цену» и «Удалить» у записи. Без `manage` (нет права `prices.write`) — только чтение.
 */
export function PriceListScreen({
  vm,
  actions,
  manage,
  typeName,
}: {
  vm: PriceListVm
  actions: PriceListActions
  manage?: { vm: PriceManageVm; actions: PriceManageActions }
  /** Цены одного вида (открыт из списка видов цен): выбора вида нет, заголовок называет вид. */
  typeName?: string
}) {
  return (
    <section className="onec-screen" aria-labelledby="onec-price-list-title">
      <header className="onec-section__head">
        <h2 id="onec-price-list-title">{typeName === undefined ? 'Цены 1С' : `Цены вида «${typeName}»`}</h2>
        <FreshnessBadge freshness={vm.freshness} />
        <Button variant="ghost" icon={<RefreshCw size={16} aria-hidden="true" />} onClick={actions.reload}>
          Обновить
        </Button>
      </header>
      {manage !== undefined && <PriceCreatePanel vm={manage.vm} actions={manage.actions} priceTypes={vm.priceTypes.filter((type) => type.value !== ALL_PRICE_TYPES)} />}
      {typeName === undefined && (
      <div className="onec-row">
        <SelectField
          label="Вид цен"
          value={vm.priceType}
          options={vm.priceTypes}
          disabled={vm.priceTypes.length === 0}
          disabledReason={vm.priceTypes.length === 0 ? 'Видов цен в срезе нет' : null}
          onChange={actions.priceTypeChosen}
        />
      </div>
      )}
      {manage !== undefined && manage.vm.remove.done !== null && (
        <p className="onec-muted" role="status">
          {manage.vm.remove.done}
        </p>
      )}
      {manage !== undefined && manage.vm.remove.error !== null && (
        <StatusMessage
          kind="error"
          title="Запись не удалена"
          text={manage.vm.remove.error.message}
          meta={`Код: ${manage.vm.remove.error.code}${manage.vm.remove.error.correlationId === null ? '' : ' · запрос ' + manage.vm.remove.error.correlationId}`}
        />
      )}
      <QueryState
        query={vm.query}
        emptyText={vm.priceTypes.length === 0 ? 'В срезе цен 1С нет ни одной цены' : 'Цен этого вида нет'}
        onRetry={actions.reload}
      />
      {vm.rows.length > 0 && (
        <>
          <div className="onec-scroll">
            <table className="onec-table">
              <caption className="onec-muted">Цен: {vm.total}</caption>
              <thead>
                <tr>
                  {typeName === undefined && <th>Вид цен</th>}
                  <th>Товар</th>
                  <th>Артикул</th>
                  <th>Цена</th>
                  <th>Дата цены</th>
                  {manage !== undefined && <th>Действия</th>}
                </tr>
              </thead>
              <tbody>
                {vm.rows.map((row) => (
                  <tr key={row.key}>
                    {typeName === undefined && <td>{row.priceTypeName}</td>}
                    <td>{row.name}</td>
                    <td>{row.sku}</td>
                    <td data-numeric="">{row.price}</td>
                    <td>{row.date}</td>
                    {manage !== undefined && (
                      <td>
                        <PriceRemoveCell rowKey={row.key} dateText={row.date} vm={manage.vm.remove} actions={manage.actions} />
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <nav className="onec-row" aria-label="Страницы списка цен">
            <Button
              variant="secondary"
              icon={<ChevronLeft size={16} aria-hidden="true" />}
              disabled={vm.page <= 0}
              onClick={() => actions.setPage(vm.page - 1)}
            >
              Назад
            </Button>
            <span>
              Страница {vm.page + 1} из {vm.pageCount}
            </span>
            <Button
              variant="secondary"
              icon={<ChevronRight size={16} aria-hidden="true" />}
              disabled={vm.page + 1 >= vm.pageCount}
              onClick={() => actions.setPage(vm.page + 1)}
            >
              Дальше
            </Button>
          </nav>
        </>
      )}
    </section>
  )
}
